import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  Self,
  inject,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  FormsModule,
  NG_VALUE_ACCESSOR,
  NgControl,
  ReactiveFormsModule,
  SelectControlValueAccessor,
  UntypedFormControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ConversationChannel } from '@vendasta/conversation';
import { parsePhoneNumber } from 'libphonenumber-js';
import { Observable, Subject, debounceTime, of, shareReplay, skip, switchMap, takeUntil } from 'rxjs';
import { InboxContact, InboxContactInfo } from '../../../../core/src/lib/interface/inbox.interface';
import { ParticipantService } from '../../../../core/src/lib/participant.service';
import { ACCOUNT_GROUP_ID_TOKEN, CONVERSATION_ROUTES_TOKEN } from '../../../../core/src/lib/tokens';
import { InboxService } from '../../../../core/src/lib/inbox.service';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { Validator } from '@vendasta/shared';

export class SelectContactControl extends FormControl<InboxContact | string> {
  constructor(value?: InboxContact | string, validator?: ValidatorFn | ValidatorFn[] | null) {
    const validators = validator ? [controlValidator(), validator].flat() : [controlValidator()];
    super(value, validators);
  }
}

function isPhoneNumberAttempt(value: string): boolean {
  return !isNaN(Number(value.replace(/[-+)(\s]/g, '')));
}

function isEmailAttempt(value: string): boolean {
  return /@.*\./.test(value);
}

@Component({
  selector: 'inbox-select-contact',
  templateUrl: './inbox-select-contact.component.html',
  styleUrls: ['./inbox-select-contact.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useClass: SelectControlValueAccessor,
      multi: true,
    },
  ],
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressBarModule,
    MatAutocompleteModule,
    GalaxyFormFieldModule,
  ],
})
export class InboxSelectContactComponent implements OnDestroy, OnInit {
  control?: SelectContactControl;
  private readonly inboxService = inject(InboxService);
  private readonly unsubscribe$ = new Subject<void>();

  contacts$?: Observable<InboxContact[]>;
  //TODO: MEGA-868 Delete this and the HTML if validation when the email channel feature is released
  readonly emailChannelSupported = toSignal(this.inboxService.canAccessEmailChannel$);

  private readonly accountGroupId = toSignal(inject(ACCOUNT_GROUP_ID_TOKEN));
  private readonly routes = toSignal(inject(CONVERSATION_ROUTES_TOKEN));

  @Output() suggestedChannel = new EventEmitter<ConversationChannel>();

  constructor(
    private router: Router,
    private participantService: ParticipantService,
    @Self() readonly ngControl: NgControl,
  ) {}

  ngOnInit(): void {
    if (!this.ngControl || !(this.ngControl.control instanceof SelectContactControl)) {
      throw new Error('CustomInputComponent: No ControlValueAccessor provided');
    }
    this.control = this.ngControl.control;

    this.contacts$ = this.control.valueChanges.pipe(
      debounceTime(200),
      takeUntil(this.unsubscribe$),
      switchMap((term) => {
        if (typeof term === 'object' && term.contactId) {
          return of([] as InboxContact[]);
        }
        const searchTerm = this.getSearchTerm(term);

        this.participantService.searchContacts(searchTerm ?? '', this.accountGroupId() ?? '');
        // skip the first value because it always starts as []
        return this.participantService.contacts$.pipe(skip(1));
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  async searchChanged(): Promise<void> {
    const contact = this.control?.value;
    if (typeof contact !== 'string' || !this.control) return;

    try {
      if (Validator.isEmail(this.control) === null && contact.length) {
        this.control.setValue({ email: contact });
        return;
      }

      const parsed = parsePhoneNumber(contact, 'US');
      if (parsed.isValid()) this.control.setValue({ phone: parsed.number });
    } catch (error) {
      return;
    }
  }

  openAddContactForm(): void {
    const routes = this.routes();
    if (!this.control || !routes) return;

    const searchTerm = this.control.value;

    let inputValue;
    if (typeof searchTerm === 'string' && isPhoneNumberAttempt(searchTerm)) {
      inputValue = { phone: searchTerm };
    }
    if (typeof searchTerm === 'string' && isEmailAttempt(searchTerm)) {
      inputValue = { email: searchTerm };
    }

    this.router.navigate([routes.root, 'inbox', 'create-contact'], {
      state: typeof searchTerm === 'object' ? { ...searchTerm } : inputValue ?? { firstName: searchTerm },
    });
  }

  displayContactInfo(contact: InboxContact): InboxContactInfo | null {
    if (!contact) return null;

    const phone = contact?.phone ? parsePhoneNumber(contact.phone, 'US') : undefined;
    const email = contact?.email;

    return {
      name: [contact.firstName, contact.lastName].filter((s) => !!s).join(' '),
      phoneAndEmail: [phone ? `+${phone?.countryCallingCode} ${phone?.formatNational()}` : undefined, email]
        .filter((s) => !!s)
        .join('  '),
    };
  }

  displayContact(contact: InboxContact): string {
    if (!contact) return '';

    const phone = contact?.phone ? parsePhoneNumber(contact.phone, 'US') : undefined;
    const email = contact?.email;

    return [
      contact.firstName,
      contact.lastName,
      phone ? `+${phone?.countryCallingCode} ${phone?.formatNational()}` : undefined,
      email,
    ]
      .filter((s) => !!s)
      .join('  ');
  }

  getPhoneSearchTerm(term: string): string {
    const cleanTerm = term.replace(/[-+)(\s]/g, '').trim();

    if (cleanTerm.startsWith('1')) return '+' + cleanTerm;

    return '+1' + cleanTerm;
  }

  getSearchTerm(term: string | InboxContact): string {
    const emailControlValue = { value: term } as AbstractControl<any, string>;
    const phoneControlValue = { value: term } as UntypedFormControl;

    switch (typeof term) {
      case 'object':
        if (term?.phone) {
          this.suggestedChannel.emit(ConversationChannel.CONVERSATION_CHANNEL_SMS);
          return term?.phone;
        }
        if (term?.email) {
          this.suggestedChannel.emit(ConversationChannel.CONVERSATION_CHANNEL_EMAIL);
          return term?.email;
        }
        return '';
      case 'string':
        if (Validator.isPhoneNumber(phoneControlValue) === null && term.length) {
          this.suggestedChannel.emit(ConversationChannel.CONVERSATION_CHANNEL_SMS);
          return this.getPhoneSearchTerm(term);
        }
        if (Validator.isEmail(emailControlValue) === null && term.length) {
          this.suggestedChannel.emit(ConversationChannel.CONVERSATION_CHANNEL_EMAIL);
          return term;
        }
        this.suggestedChannel.emit(ConversationChannel.CONVERSATION_CHANNEL_UNDEFINED);
        return term;
      default:
        this.suggestedChannel.emit(ConversationChannel.CONVERSATION_CHANNEL_UNDEFINED);
        return term;
    }
  }
}

function controlValidator(): ValidatorFn {
  //TODO: MEGA-868 Delete the service injection and validation when the email channel feature is released
  const canAccessEmailChannel = toSignal(inject(InboxService).canAccessEmailChannel$);

  return (control: AbstractControl<InboxContact | string>): ValidationErrors | null => {
    const invalidInputError = canAccessEmailChannel() ? { invalidInput: true } : { phone: true };

    const { value: contact } = control;

    if (!contact) return { invalidSelection: true };

    if (typeof contact === 'string') {
      let validPhoneNumber: boolean;
      try {
        //TODO: MEGA-1093 - use CRM shared validator instead of external library
        const parsedPhoneNumber = parsePhoneNumber(contact, 'US');
        validPhoneNumber = parsedPhoneNumber.isValid();
      } catch (error) {
        validPhoneNumber = false;
      }

      const validEmail = Validator.isEmail(control) === null;
      return validPhoneNumber || validEmail ? null : invalidInputError;
    }

    return null;
  };
}
